import { useCallback, useEffect, useMemo, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'

import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import Label from 'LuxKit/Label/Label'
import ChatMessageInput from 'tripPlanner/components/Chat/ChatMessageInput'
import { CHAT_TRIP_PLANNER_NEW_PATH } from 'tripPlanner/config'
import { buildStagedChatPromptQuery } from 'tripPlanner/utils/stagedChatPrompt'
import { appendContextTag, FLIGHTS_LANDING_PAGE_CONTEXT_LABEL } from 'tripPlanner/utils/contextTag'
import useNavigateTo from 'hooks/useNavigateTo'
import useTypingAnimation from 'hooks/useTypingAnimation'
import Group from 'components/utils/Group'
import { trackClientEvent } from 'analytics/analytics'

const TYPING_OPTIONS = {
  typingInterval: 50,
  pausingMs: 2000,
  deletingTime: 20,
}

const FLIGHTS_LANDING_PAGE_CONTEXT_MESSAGE = 'This chat started from the Luxury Escapes flights search page. The customer is likely especially interested in flights, airfare, routing, airports, date flexibility, multi-city options, and flight-led trip planning. Prefer flight-aware planning where relevant, while still answering the customer\'s actual request.'
const FLIGHTS_AI_TRIP_PLANNER_ANALYTICS_SUBJECT = 'trip_planner_on_flight_page'

function trackFlightsAiTripPlannerEvent(action: 'impression' | 'search', type: 'interaction' | 'nonInteraction') {
  trackClientEvent({
    category: 'chat_trip_planner',
    action,
    subject: FLIGHTS_AI_TRIP_PLANNER_ANALYTICS_SUBJECT,
    type,
  })
}

const SectionContainer = styled.div`
  width: 100%;
  margin-top: ${rem(28)};
`

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(16)};
  max-width: ${rem(1180)};
`

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(6)};
`

const InputWrapper = styled.div`
  width: 100%;
  border-radius: ${rem(24)};
  box-shadow: 0 0 ${rem(18)} ${rem(2)} ${props => props.theme.palette.highlight.primary.lightForeground};

  textarea::placeholder {
    font-weight: ${props => props.theme.font.primary.weight.bold};
  }
`

function FlightsAiTripPlannerPrompt() {
  const intl = useIntl()
  const navigateTo = useNavigateTo()
  const [isFocused, setIsFocused] = useState(false)

  const placeholderTexts = useMemo(() => [
    intl.formatMessage({
      id: 'OWHhbDezq6ZXsBTK0e85',
      defaultMessage: 'Try: "Plan flights from Sydney to Tokyo, Seoul and Singapore"',
      description: 'Rotating flight-related prompt example for the TripPlanner chat input on the flights landing page',
    }),
    intl.formatMessage({
      id: 'ELcANF0etZzrXbkoKrB_',
      defaultMessage: 'Try: "Find a multi-city Europe trip with flights in July"',
      description: 'Rotating multi-city flight prompt example for the TripPlanner chat input on the flights landing page',
    }),
    intl.formatMessage({
      id: 'q3ROYX1f0qH-4ipERxNy',
      defaultMessage: 'Try: "Help me fly from Melbourne to London via Singapore"',
      description: 'Rotating flight routing prompt example for the TripPlanner chat input on the flights landing page',
    }),
    intl.formatMessage({
      id: '1ufnbEazzp1XcQi2o_3g',
      defaultMessage: 'Try: "Where is cheapest to fly in Europe from Sydney in July?"',
      description: 'Rotating cheapest-destination prompt example for the TripPlanner chat input on the flights landing page',
    }),
    intl.formatMessage({
      id: 'Wiux6pTDadW5fapQx-5W',
      defaultMessage: 'Try: "Find beach destinations under $1,500 return from Brisbane"',
      description: 'Rotating budget-led beach destination prompt example for the TripPlanner chat input on the flights landing page',
    }),
    intl.formatMessage({
      id: 'nsN5_t7FMIcf-wlh1ZcP',
      defaultMessage: 'Try: "When is the cheapest time to fly to Rome?"',
      description: 'Rotating flexible-date flight prompt example for the TripPlanner chat input on the flights landing page',
    }),
    intl.formatMessage({
      id: 'MTo_RZVQFjQ7gaIz3jjJ',
      defaultMessage: 'Try: "Is it cheaper to fly to London in August or September?"',
      description: 'Rotating flight month comparison prompt example for the TripPlanner chat input on the flights landing page',
    }),
    intl.formatMessage({
      id: '0cwx_PetjYqXf2THBLN_',
      defaultMessage: 'Try: "Should I fly into Paris, Amsterdam or Frankfurt?"',
      description: 'Rotating flexible-arrival flight prompt example for the TripPlanner chat input on the flights landing page',
    }),
    intl.formatMessage({
      id: 'lcdVg0-go4OkGvGeOeXL',
      defaultMessage: 'Try: "Find the best route for London, Paris and Rome"',
      description: 'Rotating multi-city route optimisation prompt example for the TripPlanner chat input on the flights landing page',
    }),
  ], [intl])

  const typedPlaceholder = useTypingAnimation(placeholderTexts, isFocused, TYPING_OPTIONS)

  useEffect(() => {
    trackFlightsAiTripPlannerEvent('impression', 'nonInteraction')
  }, [])

  const handleSubmit = useCallback((message: string) => {
    trackFlightsAiTripPlannerEvent('search', 'interaction')
    const messageWithContext = appendContextTag(
      message,
      FLIGHTS_LANDING_PAGE_CONTEXT_MESSAGE,
      FLIGHTS_LANDING_PAGE_CONTEXT_LABEL,
    )
    navigateTo(CHAT_TRIP_PLANNER_NEW_PATH, buildStagedChatPromptQuery(messageWithContext))
  }, [navigateTo])

  const handleFocus = useCallback(() => {
    setIsFocused(true)
  }, [])

  const handleBlur = useCallback(() => {
    setIsFocused(false)
  }, [])

  return (
    <SectionContainer>
      <Content>
        <Header>
          <Group direction="horizontal" gap={8} verticalAlign="center">
            <Label kind="primary" variant="highlight">
              <FormattedMessage
                id="QO_kMoDkB-67d0pXbtmu"
                defaultMessage="New"
                description="Badge label for the AI TripPlanner prompt on the flights landing page"
              />
            </Label>
            <BodyText as="p" variant="large" weight="bold" colour="dark-contrast">
              <FormattedMessage
                id="gLg4dbTLfeKqR9cFAh9A"
                defaultMessage="Chat with Luxury Escapes"
                description="Label above the AI TripPlanner prompt on the flights landing page"
              />
            </BodyText>
          </Group>
          <Heading as="h2" variant="heading3" colour="dark-contrast">
            <FormattedMessage
              id="1nmWTinANoev-D2GGyUP"
              defaultMessage="Multi-stop. Multi-country. No problem. Plan your next trip with AI."
              description="Heading for the AI TripPlanner prompt on the flights landing page"
            />
          </Heading>
        </Header>
        <InputWrapper onFocus={handleFocus} onBlur={handleBlur}>
          <ChatMessageInput
            onSubmit={handleSubmit}
            placeholder={typedPlaceholder}
            hideDisclaimer
            autoFocus={false}
          />
        </InputWrapper>
      </Content>
    </SectionContainer>
  )
}

export default FlightsAiTripPlannerPrompt
